import { Component, inject, OnInit, signal, OnDestroy, WritableSignal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormControl,ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { LessonsDto } from '../../dtos/lessons.dto';
import { FieldsetModule } from 'primeng/fieldset';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DialogService } from 'primeng/dynamicdialog';
import { LessonsService } from '../../services/lessons.service';
import { PlatformService } from '../../../platform-service';
import { LessonsToolsMappingService } from '../../../deeplink/services/lessons-tools-mapping.service';
import { TargetLinkURICustomizationService } from '../../../deeplink/services/target-link-uri-customization.service';
import { environment } from '../../../../environments/environment';
import { ToastService } from '../../../../common/services/toast.service';
import { TableEventData } from '../../../../common/types/table.event.data.type';
import { LaunchToolDto, TestUser } from '../../../launch-tool/dtos/launch-tool.dto';


@Component({
  selector: 'app-view-lessons',
  standalone: true,
  imports: [
      TableModule,
      FieldsetModule,
      TooltipModule,
      ButtonModule,
      MessageModule,
      RouterModule,
      DatePipe,
      FloatLabelModule,
      ReactiveFormsModule,
      InputTextModule,
      IftaLabelModule,
      TooltipModule
  ],
  providers: [LessonsService,DialogService,LessonsToolsMappingService,TargetLinkURICustomizationService,ToastService],
  templateUrl: './view-lessons.html',
  styleUrls: ['./view-lessons.css'],
})
export class ViewLessonsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private isSearchTriggered = false;

  loading = signal<boolean>(false);
  lessons:Array<LessonsDto> = new Array<LessonsDto>()
  lessonsService = inject(LessonsService)
  dialogService = inject(DialogService);
  targetLinkURICustomizationService = inject(TargetLinkURICustomizationService)
  private router = inject(Router);
  toastService = inject(ToastService);
  private platformService:PlatformService = inject(PlatformService);
  selectedLesson!:LessonsDto
  private lessonsToolsMappingService = inject(LessonsToolsMappingService);
  first: number = 0;
  rows: number = 10;
  totalRecords = signal<number>(0);
  sortField = 'code';
  sortOrder = 'ASC';
  searchQuery:FormControl = new FormControl();
  apiURL:string = `${environment.platformApiURL}/duende-client/oidc-redirect?course-code=`;
  
  
  private tableEventData: WritableSignal<TableEventData>
      = signal<TableEventData>({
          skip:this.first,
          limit: this.rows,
          sortField:this.sortField,
          sortOrder:this.sortOrder
      });

  //confirmDialogRef: DynamicDialogRef | null;

  ngOnInit() {
    this.searchQuery.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value: string) => {
      if (value && value.length >= 3) {
        this.isSearchTriggered = true;
        this.first = 0;
        this.loadLessons({
          skip: 0,
          limit: this.rows,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        });
      } else if (!value || value.length === 0) {
        // Reset to show all results when search is cleared
        this.isSearchTriggered = true;
        this.first = 0;
        this.loadLessons({
          skip: 0,
          limit: this.rows,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        });
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    // Skip if this was triggered by search resetting first
    if (this.isSearchTriggered) {
      this.isSearchTriggered = false;
      return;
    }
    
    // Update stored values for use by search
    this.sortField = event.sortField as string ?? 'code';
    this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
    this.rows = event.rows ?? 10;
    
    this.loadLessons({ 
      skip: event.first ?? 0,
      limit: this.rows,
      sortField: this.sortField,
      sortOrder: this.sortOrder 
    });
  }

  async loadLessons(tableEventData:TableEventData){
    try{
      this.loading.set(true);
      const response = await this.lessonsService.getLessons({...tableEventData,searchQuery:this.searchQuery.value});
      this.lessons = response.data
      this.totalRecords.set(response.total);
      this.tableEventData.set(tableEventData);
    }catch(e){
       this.toastService.show({ severity: 'error', summary: 'Error', detail: 'There was a problem please contact tech support.' });
       throw e;
    } finally {
      this.loading.set(false);
    }
    
    
  }
  initiateDeeplink(courseCode:string){
    //TODO:  need a nice dialog here
    if(confirm("This will initiate deeplinking flow, and if this course is already linked the link will be overridden, are you sure ?")){
        this.goToToolSelectionPage(courseCode)
      }    
  }
  goToToolSelectionPage(courseCode:string){
    this.router.navigate([`/select-tool/${courseCode}`])
  }
  /*
    ⚠️ this method does a LTI launch with a test user( not a user in session from RLMS )
  */ 
  async testLaunch(courseCode:string){
        const isLinkedWithMultipleCourses = await this.getIsLinkedWithMultipleCourses(courseCode);
        if(isLinkedWithMultipleCourses){
          this.router.navigate([`/show-linked-sub-courses-test/${courseCode}`])
        }else{
          const contentItems = await this.lessonsToolsMappingService.getContentItemsForLesson(courseCode); 
          if(contentItems && Array.isArray(contentItems) && contentItems.length === 1){
            const targetLinkURI = await this.targetLinkURICustomizationService.getTargetLinkURIAsPerToolMappedTo(courseCode,contentItems[0]);
              const userInfo: LaunchToolDto = {
              ...TestUser,
              courseCode,
              custom:contentItems[0]?.custom,
              targetLinkURI
            }
            const openInNewWindow = true;
            this.platformService.initiateCourseLaunch(courseCode,userInfo,openInNewWindow)
          }else{
            console.error("contentItems variable length not correct, please check.")
          }
        }
  }
  private async getIsLinkedWithMultipleCourses(courseCode:string){
      const getLessonToolMappingResponseDto = await this.lessonsToolsMappingService.getCountOfContentItemsForLesson(courseCode);
      return getLessonToolMappingResponseDto.count > 1;
  }
  goToAddLessons(){
    this.router.navigate([`/add-lessons`])
  }
  copyCourseURL(courseCode:string){
    const text = `${this.apiURL}${courseCode}`;
    navigator.clipboard.writeText(text);
    this.toastService.show({
      severity:"success",
      summary:"Copied!",
      detail:"Copied to clipboard."
    })
  }
  async resetCanPostGradeToRLMS(lesson:LessonsDto){
    try{
      await this.lessonsService.toggleCanPostGradeToRLMS(lesson,false);
      await this.loadLessons(this.tableEventData());
      this.toastService.show({ severity: 'warn', summary: 'Done!', detail: `${lesson.code} will no longer send grades to RLMS` });
    }catch(e){
      console.error(e);
      this.toastService.show({ severity: 'error', summary: 'Error', detail: 'There can only be one lesson to send grade to RLMS. Please reset currently assigned lesson and try again.' });
    }
  }
  async setCanPostGradeToRLMS(lesson:LessonsDto){
  try{
      await this.lessonsService.toggleCanPostGradeToRLMS(lesson,true);
      await this.loadLessons(this.tableEventData());
      this.toastService.show({ severity: 'success', summary: 'Done!', detail: `${lesson.code} will send grades to RLMS` });
    }catch(e){
      console.error(e);
      this.toastService.show({ severity: 'error', summary: 'Error', detail: 'There can only be one lesson to send grade to RLMS. Please reset currently assigned lesson and try again.' });
    }
  }
}
